import { DialogComponent } from '@theme/dialog-component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { EventDebouncer } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 * Only handles opening and closing of the drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawer extends DialogComponent {
    #eventDebouncer = new EventDebouncer();

    /**
     * Called when the element is connected to the DOM.
     * Sets up event listeners for cart updates and cart additions.
     */
    connectedCallback() {
        super.connectedCallback();

        // Initialize cart cache if not already done
        this.#initializeCartCache();

        // Listen for cart updates
        document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartEvent);

        // Set up the cart icon trigger listener
        this.attachCartTriggerListener();

        // Listen for section selection in Theme Editor
        this.#setupDesignModeListener();
    }

    /**
     * Initialize cart cache manager to prevent duplicate requests
     */
    async #initializeCartCache() {
        try {
            const { CartCacheManager } = await import('@theme/cart-utilities');
            const cacheManager = CartCacheManager.getInstance();
            if (!cacheManager.isCartDataFresh()) {
                await cacheManager.initialize();
            }
        } catch (error) {
            // Silent fail - cart cache not critical for drawer functionality
        }
    }

    /**
     * Sets up listener for section selection in Theme Editor
     */
    #setupDesignModeListener() {
        if (window.Shopify.designMode) {
            document.addEventListener('shopify:section:select', this.#handleSectionSelect);
        }
    }

    /**
     * Handles section select event in Theme Editor
     */
    #handleSectionSelect = (event) => {
        const eventSectionId = event.detail.sectionId;
        if (eventSectionId && eventSectionId.includes('cart-drawer')) {
            this.open();
        }
    };

    /**
     * Called when the element is disconnected from the DOM.
     * Cleans up all event listeners to prevent memory leaks.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        // Remove cart event listener
        document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartEvent);

        // Remove cart trigger listener if it exists
        if (this.cartTrigger && this.cartTriggerListener) {
            this.cartTrigger.removeEventListener('click', this.cartTriggerListener);
        }

        // Remove design mode listener if it exists
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
            document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
        }

        // Clear all pending debounced events
        this.#eventDebouncer.clearAll();
    }

    /**
     * Handles cart updates and opens the drawer automatically based on the event source.
     * Prevents opening if already on cart page.
     */
    #handleCartEvent = (event) => {
        // Don't open if already on cart page
        if (window.location.pathname === '/cart') {
            return;
        }

        // Use EventDebouncer utility for cart event handling
        this.#eventDebouncer.debounce('cart-event', () => {
            const source = event.detail.data?.source;

            // For cart additions from product-form-component: always open
            if (source === 'product-form-component') {
                this.showDialog();
                return;
            }

            // Don't auto-open for internal cart updates (item removal, quantity change within drawer)
            if (source === 'cart-items-component') {
                return;
            }

            // For other cart updates: open only if auto-open attribute is set
            if (this.hasAttribute('auto-open')) {
                this.showDialog();
            }
        }, 50); // 50ms debounce for cart events
    };

    /**
     * Attaches a click listener to the cart icon trigger.
     * Finds the element with 'data-cart-drawer-trigger' attribute and
     * makes it open the cart drawer when clicked.
     * Prevents opening if already on cart page.
     */
    attachCartTriggerListener() {
        const trigger = document.querySelector('[data-cart-drawer-trigger]');
        if (trigger) {
            this.cartTrigger = trigger;
            this.cartTriggerListener = (event) => {
                // Check if we're on the cart page
                if (window.location.pathname === '/cart') {
                    // Prevent opening drawer on cart page
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }
                this.open();
            };
            trigger.addEventListener('click', this.cartTriggerListener);
        }
    }

    /**
     * Public method to open the cart drawer.
     * Can be called from other components or directly.
     */
    open() {
        this.showDialog();
    }

    /**
     * Public method to close the cart drawer.
     * Can be called from other components or directly.
     */
    close() {
        this.closeDialog();
    }
}

// Register the custom element if it hasn't been registered yet
if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
} 